import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)




export function hero() {

	const section = document.querySelector('.section--hero')
	if (!section) return

	const items      = section.querySelectorAll('.hero')
	const prefersRed = window.matchMedia('(prefers-reduced-motion: reduce)').matches
	if (prefersRed) return

	// ── shared eases ────────────────────────────────────────────────────────
	const ELASTIC  = 'elastic.out(1, 0.45)'

	// ── helper: compute the natural font-size value from the CSS clamp ───────
	function naturalFontSize(el) {
		return parseFloat(getComputedStyle(el).fontSize)
	}

	// ── per-item scroll-scrubbed entrance ────────────────────────────────────
	items.forEach((item, i) => {
		const heading = item.querySelector('.hero__heading')
		const tagline = item.querySelector('.hero__tagline')
		const media   = item.querySelector('.hero__media')

		if (!heading || !media) return

		const fullSize = naturalFontSize(heading)

		// set initial hidden state
		gsap.set(heading, { fontSize: fullSize * 0.55, opacity: 0 })
		gsap.set(tagline,  { opacity: 0, y: 16 })
		gsap.set(media,    { opacity: 0, y: 80 })

		const startOffset = '90%'
		const endOffset   = '50%'

		// each successive item gets a slightly larger scrub lag
		const baseScrub   = 1
		const scrubStep   = 0.35
		const headingScrub = baseScrub + i * scrubStep
		const taglineScrub = headingScrub + 0.4
		const circleScrub  = headingScrub + 0.7

		// heading
		gsap.timeline({
			scrollTrigger: { trigger: item, start: `top ${startOffset}`, end: `top ${endOffset}`, scrub: headingScrub }
		}).to(heading, { fontSize: fullSize, opacity: 1, ease: 'power2.out' })

		// tagline — elastic lag like the circle
		gsap.timeline({
			scrollTrigger: { trigger: item, start: `top ${parseInt(startOffset) - 4}%`, end: `top ${parseInt(endOffset) - 6}%`, scrub: taglineScrub }
		}).to(tagline, { opacity: 1, y: 0, ease: 'power3.out' })

		// circle — most lag
		gsap.timeline({
			scrollTrigger: { trigger: item, start: `top ${parseInt(startOffset) - 8}%`, end: `top ${parseInt(endOffset) - 12}%`, scrub: circleScrub }
		}).to(media, { opacity: 1, y: 0, ease: 'power3.out' })
	})


	// ── mouse / proximity magnetic on circles ───────────────────────────────
	const allMedia = section.querySelectorAll('.hero__media')

	allMedia.forEach(media => {
		const circle = media.querySelector('.hero__media__circle')
		const img    = media.querySelector('img, video, picture')

		if (!circle) return

		// approximate connector line height for angle calculation
		// --hero-line-gap is roughly 6vw, clamped between 3rem–5.5rem
		const lineGapH = () => Math.min(window.innerWidth * 0.06, 88)

		let rafId    = null
		let currentX = 0
		let currentY = 0
		let targetX  = 0
		let targetY  = 0

		function lerp(a, b, t) { return a + (b - a) * t }

		function tick() {
			currentX = lerp(currentX, targetX, 0.08)
			currentY = lerp(currentY, targetY, 0.08)

			// circle moves in x and y
			gsap.set(circle, { x: currentX, y: currentY })

			// real geometry: each line points from its anchor dot to the moved circle
			const lh = lineGapH()
			const topDy  = lh + currentY
			const botDy  = lh - currentY
			const aTop   = Math.atan2(-currentX, topDy)   // ::before hangs down; negate X
			const aBot   = Math.atan2(currentX, botDy)
			const sTop   = Math.sqrt(currentX * currentX + topDy * topDy) / lh
			const sBot   = Math.sqrt(currentX * currentX + botDy * botDy) / lh

			media.style.setProperty('--hero-angle-top',  `${aTop}rad`)
			media.style.setProperty('--hero-angle-bot',  `${aBot}rad`)
			media.style.setProperty('--hero-scale-top',  sTop)
			media.style.setProperty('--hero-scale-bot',  sBot)

			if (img) {
				gsap.set(img, { x: currentX * -0.3, y: currentY * -0.3 })
			}

			if (Math.abs(currentX - targetX) > 0.05 || Math.abs(currentY - targetY) > 0.05) {
				rafId = requestAnimationFrame(tick)
			} else {
				rafId = null
			}
		}

		function onMouseMove(e) {
			// always re-fetch — scroll and GSAP transforms shift the element
			const bounds = media.getBoundingClientRect()
			const cx = bounds.left + bounds.width  / 2
			const cy = bounds.top  + bounds.height / 2
			const dx = e.clientX - cx
			const dy = e.clientY - cy
			const dist = Math.hypot(dx, dy)
			const radius = bounds.width * 0.9

			if (dist < radius) {
				const strength = (1 - dist / radius)
				const maxPull  = 22
				targetX = (dx / dist) * strength * maxPull
				targetY = (dy / dist) * strength * maxPull
			} else {
				targetX = 0
				targetY = 0
			}

			if (!rafId) rafId = requestAnimationFrame(tick)
		}

		function onMouseLeave() {
			targetX = 0
			targetY = 0
			// reset connector CSS vars immediately
			media.style.setProperty('--hero-angle-top', '0rad')
			media.style.setProperty('--hero-angle-bot', '0rad')
			media.style.setProperty('--hero-scale-top', '1')
			media.style.setProperty('--hero-scale-bot', '1')
			if (!rafId) rafId = requestAnimationFrame(tick)
		}

		window.addEventListener('mousemove', onMouseMove, { passive: true })
		media.addEventListener('mouseleave', onMouseLeave)
	})


	// ── scroll: elastic y-lag on circles (line stretches, snaps back) ────────
	allMedia.forEach(media => {
		let lagY = 0

		ScrollTrigger.create({
			trigger: media,
			start: 'top bottom',
			end: 'bottom top',
			onUpdate(self) {
				const vel  = self.getVelocity()              // px/s
				const pull = gsap.utils.clamp(-30, 30, vel * 0.018)

				// circle drifts in scroll direction as if the line is stretching
				gsap.to(media, {
					y: lagY + pull,
					duration: 0.4,
					ease: 'power2.out',
					overwrite: 'auto',
				})
			},
			onLeave()     { gsap.to(media, { y: 0, duration: 1.1, ease: ELASTIC, overwrite: 'auto' }) },
			onLeaveBack() { gsap.to(media, { y: 0, duration: 1.1, ease: ELASTIC, overwrite: 'auto' }) },
		})
	})
}
